export const DEV_BASE_URL = "http://localhost:8080";

/** Clave de desarrollo: solo aplica en localhost. En producción la puerta del
 * panel arranca vacía y el operador ingresa su API key. */
export function defaultApiKey(): string {
  return isLocalDevelopment() ? "dev-operator-key" : "";
}

const LOCAL_HOSTNAMES: readonly string[] = ["localhost", "127.0.0.1"];

export interface AppConfig {
  baseUrl: string;
  apiKey: string;
}

const STORAGE_KEY = "ecotracker.web.config.v1";

export function isLocalHostname(hostname: string): boolean {
  return LOCAL_HOSTNAMES.includes(hostname);
}

export function isLocalDevelopment(): boolean {
  return isLocalHostname(window.location.hostname);
}

export function isLocalhostUrl(value: string): boolean {
  try {
    return isLocalHostname(new URL(value).hostname);
  } catch {
    return false;
  }
}

export function effectiveBaseUrl(): string {
  return isLocalDevelopment() ? DEV_BASE_URL : window.location.origin;
}

export function defaultConfig(): AppConfig {
  return { baseUrl: effectiveBaseUrl(), apiKey: defaultApiKey() };
}

export function normalizeBaseUrl(value: string): string {
  return value.trim().replace(/\/+$/, "");
}

export function isValidBaseUrl(value: string): boolean {
  const normalized = normalizeBaseUrl(value);
  if (normalized === "") return false;
  try {
    const url = new URL(normalized);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

export function loadConfig(): AppConfig {
  const fallback = defaultConfig();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return fallback;
    const record = parsed as Record<string, unknown>;
    const baseUrl = typeof record["baseUrl"] === "string" ? record["baseUrl"] : fallback.baseUrl;
    const apiKey = typeof record["apiKey"] === "string" ? record["apiKey"] : fallback.apiKey;
    const normalized = normalizeBaseUrl(baseUrl) || fallback.baseUrl;
    if (!isLocalDevelopment() && isLocalhostUrl(normalized)) {
      return { baseUrl: fallback.baseUrl, apiKey };
    }
    return { baseUrl: normalized, apiKey };
  } catch {
    return fallback;
  }
}

export function saveConfig(config: AppConfig): void {
  try {
    const payload: AppConfig = { baseUrl: normalizeBaseUrl(config.baseUrl), apiKey: config.apiKey };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    return;
  }
}
